import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FaBible,
  FaChevronLeft,
  FaChevronRight,
  FaCopy,
  FaFont,
  FaStickyNote,
  FaTimes,
} from 'react-icons/fa';
import { toast } from 'react-toastify';

import {
  BIBLE_PARALLEL_MODE_OPTIONS,
  buildBlackboardScripturePayload,
  fetchParallelBibleChapter,
  getBibleTranslation,
  getParallelBibleMode,
} from '../../utils/bibleApi';
import {
  dispatchBlackboardScriptureInsert,
} from '../../utils/blackboardScriptureBridge';

const createNumbers = (count) =>
  Array.from({ length: Math.max(0, Number(count) || 0) }, (_, index) => index + 1);

const BiblePanel = ({ onClose, onInsertToBlackboard }) => {
  const [modeId, setModeId] = useState('nt');
  const [book, setBook] = useState('John');
  const [chapter, setChapter] = useState(3);
  const [verse, setVerse] = useState('');
  const [chapterData, setChapterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryNonce, setRetryNonce] = useState(0);
  const [inserting, setInserting] = useState(false);
  const [insertStatus, setInsertStatus] = useState('');
  const [keepOpenForMultiple, setKeepOpenForMultiple] = useState(false);
  const insertionInFlightRef = useRef(false);

  const parallelMode = useMemo(() => getParallelBibleMode(modeId), [modeId]);
  const standalone = parallelMode.standalone === true;
  const books = parallelMode.books;
  const bookInfo = useMemo(
    () => books.find((item) => item.name === book) || books[0],
    [book, books]
  );
  const originalTranslation = useMemo(
    () => getBibleTranslation(parallelMode.originalTranslationId),
    [parallelMode]
  );
  const englishTranslation = useMemo(
    () => parallelMode.englishTranslationId
      ? getBibleTranslation(parallelMode.englishTranslationId)
      : null,
    [parallelMode]
  );
  const chapterNumbers = useMemo(
    () => createNumbers(bookInfo?.chapters),
    [bookInfo]
  );
  const visibleVerses = useMemo(() => {
    const verses = chapterData?.verses || [];
    if (verse === '') return verses;
    return verses.filter((item) => item.verse === Number(verse));
  }, [chapterData, verse]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    let timedOut = false;
    const timeout = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 20000);

    setLoading(true);
    setError('');
    setChapterData(null);
    setVerse('');

    fetchParallelBibleChapter(modeId, book, chapter, {
      signal: controller.signal,
    })
      .then((result) => {
        if (active) setChapterData(result);
      })
      .catch((requestError) => {
        if (!active) return;
        if (timedOut) {
          setError('Bible sources timed out. Check the connection and retry.');
        } else if (requestError?.name !== 'AbortError') {
          setError(
            requestError?.message ||
              'Bible verses could not be loaded. Check the connection and retry.'
          );
        }
      })
      .finally(() => {
        window.clearTimeout(timeout);
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [book, chapter, modeId, retryNonce]);

  const changeParallelMode = (nextModeId) => {
    const next = getParallelBibleMode(nextModeId);
    const nextBook =
      next.books.find((item) => item.name === next.defaultBook) || next.books[0];
    setModeId(next.id);
    setBook(nextBook?.name || '');
    setChapter(Math.min(next.defaultChapter || 1, nextBook?.chapters || 1));
    setVerse('');
  };

  const moveChapter = (direction) => {
    const currentBookIndex = books.findIndex((item) => item.name === book);
    if (direction < 0 && chapter > 1) {
      setChapter((value) => value - 1);
      return;
    }
    if (direction > 0 && chapter < bookInfo.chapters) {
      setChapter((value) => value + 1);
      return;
    }

    const nextBook = books[currentBookIndex + direction];
    if (!nextBook) return;
    setBook(nextBook.name);
    setChapter(direction < 0 ? nextBook.chapters : 1);
  };

  const buildVisiblePayload = (insertMode = 'text', selectedVerse = verse) =>
    buildBlackboardScripturePayload(chapterData, {
      mode: insertMode,
      verse: selectedVerse === '' ? null : selectedVerse,
    });

  const copyVisibleText = async () => {
    if (!visibleVerses.length) return;
    const text = buildVisiblePayload('text').text;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand('copy');
        textarea.remove();
        if (!copied) throw new Error('Copy command failed');
      }
      toast.success('Bible passage copied with source attribution.');
    } catch {
      toast.error('Unable to copy this passage.');
    }
  };

  const insertIntoBlackboard = async (insertMode, selectedVerse = null) => {
    if (insertionInFlightRef.current) return;
    insertionInFlightRef.current = true;
    setInserting(true);
    setInsertStatus('Opening or restoring the Blackboard and adding this passage…');
    try {
      const payload = buildVisiblePayload(insertMode, selectedVerse);
      let outcome;
      if (typeof onInsertToBlackboard === 'function') {
        outcome = await onInsertToBlackboard(payload);
      } else {
        outcome = await dispatchBlackboardScriptureInsert(payload);
      }
      const message = `${payload.reference} added as editable Blackboard ${insertMode}.`;
      if (outcome?.queued) toast.info(`${message} It will sync after reconnection.`);
      else toast.success(message);
      if (keepOpenForMultiple) {
        setInsertStatus(`${message} Choose another verse or chapter to add.`);
      } else {
        setInsertStatus('');
        onClose?.();
      }
    } catch (insertError) {
      setInsertStatus('');
      toast.error(insertError?.message || 'Unable to add this passage to the Blackboard.');
    } finally {
      insertionInFlightRef.current = false;
      setInserting(false);
    }
  };

  const firstBook = books[0];
  const lastBook = books[books.length - 1];
  const selectedReference = `${book} ${chapter}${verse === '' ? '' : `:${verse}`}`;

  return (
    <aside
      className="absolute inset-y-0 right-0 z-50 flex w-full flex-col border-l border-amber-300/20 bg-slate-950/[0.98] text-slate-100 shadow-2xl backdrop-blur-xl md:relative md:w-[760px] md:max-w-[68vw]"
      aria-label={`${parallelMode.name} Bible`}
    >
      <div className="border-b border-white/10 bg-gradient-to-r from-amber-500/20 via-yellow-300/10 to-transparent px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-amber-400 text-slate-950 shadow-lg shadow-amber-500/20">
              <FaBible size={20} />
            </span>
            <div className="min-w-0">
              <h2 className="truncate text-base font-semibold text-white">
                {standalone ? 'Holy Bible · WEB' : 'Parallel Holy Bible'}
              </h2>
              <p className="truncate text-[11px] text-amber-100/75">
                {standalone
                  ? 'Free public-domain English Scripture'
                  : 'Original-language Scripture aligned verse by verse with English'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-300 transition hover:bg-white/10 hover:text-white"
            aria-label="Close Bible"
          >
            <FaTimes />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 border-b border-white/10 bg-slate-900/70 p-3 sm:grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)_100px_105px]">
        <label className="col-span-2 text-[11px] font-medium uppercase tracking-wide text-slate-400 sm:col-span-1">
          Bible edition
          <select
            value={modeId}
            onChange={(event) => changeParallelMode(event.target.value)}
            className="mt-1 w-full rounded-lg border border-white/10 bg-slate-800 px-3 py-2 text-sm normal-case text-white outline-none focus:border-amber-400"
          >
            {BIBLE_PARALLEL_MODE_OPTIONS.map((item) => (
              <option key={item.id} value={item.id}>{item.shortName}</option>
            ))}
          </select>
        </label>

        <label className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
          Book
          <select
            value={book}
            onChange={(event) => {
              setBook(event.target.value);
              setChapter(1);
            }}
            className="mt-1 w-full rounded-lg border border-white/10 bg-slate-800 px-3 py-2 text-sm normal-case text-white outline-none focus:border-amber-400"
          >
            {books.map((item) => (
              <option key={item.name} value={item.name}>{item.name}</option>
            ))}
          </select>
        </label>

        <label className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
          Chapter
          <select
            value={chapter}
            onChange={(event) => setChapter(Number(event.target.value))}
            className="mt-1 w-full rounded-lg border border-white/10 bg-slate-800 px-3 py-2 text-sm normal-case text-white outline-none focus:border-amber-400"
          >
            {chapterNumbers.map((number) => (
              <option key={number} value={number}>{number}</option>
            ))}
          </select>
        </label>

        <label className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
          Verse
          <select
            value={verse}
            disabled={!chapterData?.verses?.length}
            onChange={(event) =>
              setVerse(event.target.value === '' ? '' : Number(event.target.value))
            }
            className="mt-1 w-full rounded-lg border border-white/10 bg-slate-800 px-3 py-2 text-sm normal-case text-white outline-none focus:border-amber-400 disabled:opacity-50"
          >
            <option value="">Full chapter</option>
            {(chapterData?.verses || []).map((item) => (
              <option key={item.verse} value={item.verse}>
                Verse {item.verseLabel || item.verse}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 px-3 py-2">
        <button
          type="button"
          onClick={() => moveChapter(-1)}
          disabled={book === firstBook?.name && chapter === 1}
          className="rounded-lg p-2 text-slate-300 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30"
          aria-label="Previous chapter"
        >
          <FaChevronLeft />
        </button>
        <div className="min-w-0 flex-1 text-center">
          <p className="truncate text-sm font-semibold text-amber-200">
            {selectedReference}
          </p>
          <p className="truncate text-[10px] text-slate-400">
            {standalone
              ? `${originalTranslation.shortName} · standalone English · Public Domain`
              : `${originalTranslation.shortName} + ${englishTranslation.shortName} · verse-aligned`}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={copyVisibleText}
            disabled={!visibleVerses.length}
            className="rounded-lg p-2 text-slate-300 transition hover:bg-white/10 disabled:opacity-30"
            aria-label="Copy displayed Bible passage"
            title="Copy with attribution"
          >
            <FaCopy />
          </button>
          <button
            type="button"
            onClick={() => insertIntoBlackboard('text', null)}
            disabled={!chapterData?.verses?.length || inserting}
            className="inline-flex items-center gap-1 rounded-lg border border-sky-400/30 bg-sky-400/10 px-2 py-2 text-[11px] font-semibold text-sky-100 transition hover:bg-sky-400/20 disabled:opacity-30"
            aria-label="Add full chapter to Blackboard as editable text"
          >
            <FaFont /> Chapter
          </button>
          <button
            type="button"
            onClick={() => insertIntoBlackboard('sticky', null)}
            disabled={!chapterData?.verses?.length || inserting}
            className="inline-flex items-center gap-1 rounded-lg border border-yellow-400/30 bg-yellow-400/10 px-2 py-2 text-[11px] font-semibold text-yellow-100 transition hover:bg-yellow-400/20 disabled:opacity-30"
            aria-label="Add full chapter to Blackboard as editable sticky note"
          >
            <FaStickyNote /> Chapter
          </button>
          <button
            type="button"
            onClick={() => moveChapter(1)}
            disabled={book === lastBook?.name && chapter === bookInfo?.chapters}
            className="rounded-lg p-2 text-slate-300 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30"
            aria-label="Next chapter"
          >
            <FaChevronRight />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 bg-slate-950/70 px-3 py-2">
        <label className="flex items-center gap-2 text-[11px] font-medium text-slate-300">
          <input
            type="checkbox"
            checked={keepOpenForMultiple}
            onChange={(event) => setKeepOpenForMultiple(event.target.checked)}
          />
          Keep Bible open for multiple inserts
        </label>
        {insertStatus && (
          <span
            role="status"
            aria-live="polite"
            className="rounded-full bg-sky-500/15 px-3 py-1 text-[11px] font-semibold text-sky-100"
          >
            {insertStatus}
          </span>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3">
        {loading && (
          <div className="grid h-40 place-items-center text-center">
            <div>
              <span className="mx-auto block h-7 w-7 animate-spin rounded-full border-2 border-amber-300 border-t-transparent" />
              <p className="mt-3 text-sm text-slate-400">
                Loading {standalone
                  ? originalTranslation.shortName
                  : `${originalTranslation.shortName} + ${englishTranslation.shortName}`}…
              </p>
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-4 text-sm text-red-100">
            <p>{error}</p>
            <button
              type="button"
              onClick={() => setRetryNonce((value) => value + 1)}
              className="mt-3 rounded-lg bg-red-500/20 px-3 py-2 font-medium hover:bg-red-500/30"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <div className="space-y-3">
            <div className={`grid grid-cols-1 gap-2 ${standalone ? '' : 'md:grid-cols-2'}`} aria-hidden="true">
              {[originalTranslation, ...(englishTranslation ? [englishTranslation] : [])].map((translation) => (
                <div
                  key={translation.id}
                  className="rounded-lg border border-amber-300/15 bg-amber-300/[0.04] px-3 py-2"
                >
                  <p className="text-xs font-semibold text-amber-200">
                    {translation.shortName} · {translation.language}
                  </p>
                  <p className="text-[10px] text-slate-400">
                    {translation.attribution} · {translation.license}
                  </p>
                </div>
              ))}
            </div>

            {visibleVerses.map((row) => (
              <article
                key={row.verse}
                className="overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.035] shadow-sm"
                aria-label={row.reference}
              >
                <div className="flex items-center justify-between border-b border-white/[0.07] bg-slate-900/65 px-3 py-2">
                  <strong className="text-xs text-amber-300">{row.reference}</strong>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => insertIntoBlackboard('text', row.verse)}
                      disabled={inserting}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-semibold text-sky-200 hover:bg-sky-400/15"
                      aria-label={`Add ${row.reference} to Blackboard as editable text`}
                    >
                      <FaFont /> Text
                    </button>
                    <button
                      type="button"
                      onClick={() => insertIntoBlackboard('sticky', row.verse)}
                      disabled={inserting}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-semibold text-yellow-200 hover:bg-yellow-400/15"
                      aria-label={`Add ${row.reference} to Blackboard as editable sticky note`}
                    >
                      <FaStickyNote /> Sticky
                    </button>
                  </div>
                </div>
                <div className={`grid grid-cols-1 divide-y divide-white/[0.07] ${standalone ? '' : 'md:grid-cols-2 md:divide-x md:divide-y-0'}`}>
                  <div
                    className={`min-h-[96px] px-3 py-3 text-[17px] leading-8 text-slate-100 ${
                      originalTranslation.direction === 'rtl' ? 'text-right' : 'text-left'
                    }`}
                    dir={originalTranslation.direction}
                    lang={originalTranslation.languageCode}
                    style={{
                      fontFamily: originalTranslation.fontFamily,
                      unicodeBidi: 'plaintext',
                    }}
                  >
                    {row.original?.text || (
                      <span className="text-sm italic text-slate-500">
                        Verse not available in {originalTranslation.shortName}.
                      </span>
                    )}
                  </div>
                  {!standalone && (
                    <div
                      className="min-h-[96px] px-3 py-3 text-left text-[16px] leading-7 text-slate-200"
                      dir="ltr"
                      lang="en"
                      style={{
                        fontFamily: englishTranslation.fontFamily,
                        unicodeBidi: 'plaintext',
                      }}
                    >
                      {row.english?.text || (
                        <span className="text-sm italic text-slate-500">
                          Verse not available in {englishTranslation.shortName}.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-white/10 bg-slate-900/80 px-4 py-2 text-center text-[10px] leading-4 text-slate-400">
        <a
          href={originalTranslation.sourceUrl}
          target="_blank"
          rel="noreferrer"
          className="font-semibold text-slate-300 underline decoration-slate-600 underline-offset-2"
        >
          {originalTranslation.attribution}
        </a>
        {' · '}
        <a
          href={originalTranslation.licenseUrl}
          target="_blank"
          rel="noreferrer"
          className="text-slate-300 underline decoration-slate-600 underline-offset-2"
        >
          {originalTranslation.license}
        </a>
        {englishTranslation && (
          <>
            {' · '}
            <a
              href={englishTranslation.sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-slate-300 underline decoration-slate-600 underline-offset-2"
            >
              {englishTranslation.attribution}
            </a>
            {' · '}{englishTranslation.license}
          </>
        )}
      </div>
    </aside>
  );
};

export default BiblePanel;
